import { paintIdSchema } from "@frontier/content/season";
import { schema, t, type SchemaType } from "@colyseus/schema";
import type { ShipSnapshot, WorldSnapshot } from "./contracts.js";
import { equipmentTierSchema, hullIdSchema, npcAttackKindSchema, publicEventTypeSchema } from "./contracts.js";
import { publicEventPresentationSchema } from "@frontier/content/event-scenarios";
import { relayCaptureViewSchema } from "./relay-contracts.js";
import { RELAY_PRESTIGE_ID } from "@frontier/content/relay";
import type { PublicEventStatus, PublicEventView } from "@frontier/content/events";
import { z } from "zod";
import { SECTOR_IDS } from "@frontier/content/contracts";
import type { FactionId } from "@frontier/content/factions";
import { factionIdSchema } from "./auth-contracts.js";
import { TRAINING_GATE_FACTIONS } from "@frontier/content/training";
import { flightControlsSchema, npcAttackGeometrySchema } from "@frontier/content/combat-rules";

export const ShipState = schema(
  {
    id: t.string().default(""),
    controlsJson: t.string().default(""),
    lossDisposition: t.string().default(""),
    attackGeometryJson: t.string().default(""),
    name: t.string().default(""),
    paintId: t.string().default("default"),
    factionId: t.string<FactionId | "">().default(""),
    kind: t.string().default(""),
    archetype: t.string().default(""),
    sectorId: t.string().default("patrol"),
    passiveId: t.string().default("none"),
    x: t.float64().default(0),
    y: t.float64().default(0),
    angle: t.float64().default(0),
    hp: t.float64().default(0),
    maxHp: t.float64().default(0),
    shield: t.float64().default(0),
    maxShield: t.float64().default(0),
    energy: t.float64().default(0),
    maxEnergy: t.float64().default(0),
    cargo: t.number().default(0),
    energyCargo: t.number().default(0),
    salvageCargo: t.number().default(0),
    contrabandCargo: t.number().default(0),
    pvpEnabled: t.boolean().default(false),
    capacity: t.number().default(3),
    docked: t.boolean().default(false),
    dead: t.boolean().default(false),
    pending: t.boolean().default(false),
    loadoutId: t.string().default(""),
    hullId: t.string().default("assault"),
    weaponId: t.string().default(""),
    moduleId: t.string().default(""),
    secondaryModuleId: t.string().default(""),
    weaponTier: t.number().default(0),
    moduleTier: t.number().default(0),
    secondaryModuleTier: t.number().default(0),
    passiveTier: t.number().default(0),
    targetId: t.string().default(""),
    abilityCooldown: t.float64().default(0),
    secondaryCooldown: t.float64().default(0),
    thirdCooldown: t.float64().default(0),
    abilityCooldowns: t.array("number"),
    boosting: t.boolean().default(false),
    radius: t.float64().default(20),
    telegraph: t.float64().default(0),
    jammingRemaining: t.float64().default(0),
    attackKind: t.string().default("none"),
    attackX: t.float64().default(0),
    attackY: t.float64().default(0),
    wanted: t.boolean().default(false),
    securityTargetId: t.string().default(""),
  },
  "FrontierShip",
);
export type ShipState = SchemaType<typeof ShipState>;
export const LootState = schema(
  {
    id: t.string().default(""),
    kind: t.string().default(""),
    x: t.float64().default(0),
    y: t.float64().default(0),
    value: t.number().default(0),
    pending: t.boolean().default(false),
    claimedBy: t.array("string"),
    shared: t.boolean().default(false),
  },
  "FrontierLoot",
);
export type LootState = SchemaType<typeof LootState>;
export const ProjectileState = schema(
  {
    id: t.string().default(""),
    x: t.float64().default(0),
    y: t.float64().default(0),
    angle: t.float64().default(0),
    kind: t.string().default(""),
    ownerId: t.string().default(""),
  },
  "FrontierProjectile",
);
export type ProjectileState = SchemaType<typeof ProjectileState>;
export const CombatEffectState = schema(
  {
    id: t.string().default(""),
    kind: t.string().default("scan"),
    ownerId: t.string().default(""),
    x: t.float64().default(0),
    y: t.float64().default(0),
    radius: t.float64().default(0),
    remaining: t.float64().default(0),
  },
  "FrontierCombatEffect",
);
export type CombatEffectState = SchemaType<typeof CombatEffectState>;
export const PublicEventState = schema({
  id: t.string().default(""),
  type: t.string().default("escort"),
  title: t.string().default(""),
  termsVersion: t.string().default(""),
  variantId: t.string().default(""),
  description: t.string().default(""),
  condition: t.string().default(""),
  credits: t.string().default(""),
  components: t.number().default(0),
  contributionDamage: t.float64().default(0),
  contributionHealing: t.float64().default(0),
  contributionTasks: t.float64().default(0),
  workBlocked: t.boolean().default(false),
  captureFaction: t.string().default(""),
  captureContested: t.boolean().default(false),
  captureRadius: t.float64().default(0),
  prestigeReward: t.string().default(""),
  sectorId: t.string().default("patrol"),
  status: t.string().default("available"),
  x: t.float64().default(0), y: t.float64().default(0),
  progress: t.float64().default(0), goal: t.float64().default(1),
  participants: t.number().default(0), scaledFor: t.number().default(1),
  remaining: t.float64().default(0),
  objectiveName: t.string().default(""),
  objectiveX: t.float64().default(0), objectiveY: t.float64().default(0),
  objectiveKind: t.string().default("travel"), targetId: t.string().default(""),
  hasDeadline: t.boolean().default(true),
}, "FrontierPublicEvent");
export type PublicEventState = SchemaType<typeof PublicEventState>;
export const ObstacleState = schema(
  {
    resourceHp: t.float64().default(0),
    resourceMaxHp: t.float64().default(0),
    id: t.string().default(""),
    x: t.float64().default(0),
    y: t.float64().default(0),
    radius: t.float64().default(0),
  },
  "FrontierObstacle",
);
export type ObstacleState = SchemaType<typeof ObstacleState>;
export const GateState = schema(
  {
    id: t.string().default(""),
    name: t.string().default(""),
    x: t.float64().default(0),
    y: t.float64().default(0),
    radius: t.float64().default(0),
    toSectorId: t.string().default(""),
  },
  "FrontierGate",
);
export type GateState = SchemaType<typeof GateState>;
export const RegionState = schema(
  {
    factionId: t.string().default(""),
    pvpAllowed: t.boolean().default(false),
    description: t.string().default(""),
    resourceSummary: t.string().default(""),
    id: t.string().default(""),
    name: t.string().default(""),
    x: t.float64().default(0),
    y: t.float64().default(0),
    width: t.float64().default(0),
    height: t.float64().default(0),
    weather: t.string().default(""),
    variant: t.string().default(""),
  },
  "FrontierRegion",
);
export type RegionState = SchemaType<typeof RegionState>;
export const BeaconState = schema({
  id: t.string().default(""),
  x: t.float64().default(0), y: t.float64().default(0),
  radius: t.float64().default(0), expiresAt: t.float64().default(0),
}, "FrontierBeacon");
export type BeaconState = SchemaType<typeof BeaconState>;
export const SectorState = schema(
  {
    tick: t.number().default(0),
    time: t.float64().default(0),
    event: t.string().default(""),
    eventRemaining: t.float64().default(0),
    stationX: t.float64().default(0),
    stationY: t.float64().default(0),
    width: t.float64().default(3200),
    height: t.float64().default(2200),
    players: t.map(ShipState).view(),
    npcs: t.map(ShipState).view(),
    loot: t.map(LootState).view(),
    projectiles: t.map(ProjectileState).view(),
    combatEffects: t.map(CombatEffectState).view(),
    beacons: t.map(BeaconState),
    publicEvents: t.map(PublicEventState),
    obstacles: t.map(ObstacleState),
    gates: t.map(GateState),
    regions: t.map(RegionState),
  },
  "FrontierSector",
);
export type SectorState = SchemaType<typeof SectorState>;

function shipSnapshot(ship: ShipState): ShipSnapshot {
  return {
    ...(ship.lossDisposition ? { lossDisposition: z.enum(["keep", "destroy", "drop"]).parse(ship.lossDisposition) } : {}),
    ...(ship.attackGeometryJson ? { attackGeometry: npcAttackGeometrySchema.parse(JSON.parse(ship.attackGeometryJson)) } : {}),
    ...(ship.controlsJson ? { controls: flightControlsSchema.parse(JSON.parse(ship.controlsJson)) } : {}),
    id: ship.id,
    name: ship.name,
    paintId: paintIdSchema.parse(ship.paintId),
    factionId: ship.factionId === "" ? null : factionIdSchema.parse(ship.factionId),
    kind: ship.kind,
    wanted: ship.wanted,
    ...(ship.securityTargetId ? { securityTargetId: ship.securityTargetId } : {}),
    archetype: ship.archetype,
    sectorId: ship.sectorId,
    passiveId: ship.passiveId,
    x: ship.x,
    y: ship.y,
    angle: ship.angle,
    hp: ship.hp,
    maxHp: ship.maxHp,
    shield: ship.shield,
    maxShield: ship.maxShield,
    energy: ship.energy,
    maxEnergy: ship.maxEnergy,
    cargo: ship.cargo,
    energyCargo: ship.energyCargo,
    salvageCargo: ship.salvageCargo,
    contrabandCargo: ship.contrabandCargo,
    pvpEnabled: ship.pvpEnabled,
    capacity: ship.capacity,
    docked: ship.docked,
    dead: ship.dead,
    pending: ship.pending,
    loadoutId: ship.loadoutId,
    hullId: hullIdSchema.parse(ship.hullId),
    weaponId: ship.weaponId,
    moduleId: ship.moduleId,
    secondaryModuleId: ship.secondaryModuleId,
    equipmentTiers: {
      weapon: equipmentTierSchema.parse(ship.weaponTier),
      module: equipmentTierSchema.parse(ship.moduleTier),
      secondaryModule: equipmentTierSchema.parse(ship.secondaryModuleTier),
      passive: equipmentTierSchema.parse(ship.passiveTier),
    },
    targetId: ship.targetId,
    abilityCooldown: ship.abilityCooldown,
    secondaryCooldown: ship.secondaryCooldown,
    thirdCooldown: ship.thirdCooldown,
    abilityCooldowns: Array.from(ship.abilityCooldowns),
    boosting: ship.boosting,
    radius: ship.radius,
    telegraph: ship.telegraph,
    jammingRemaining: ship.jammingRemaining,
    attackKind: npcAttackKindSchema.parse(ship.attackKind),
    attackX: ship.attackX,
    attackY: ship.attackY,
  };
}
export function toWorldSnapshot(state: SectorState, viewerId?: string): WorldSnapshot {
  const viewer = viewerId ? state.players.get(viewerId) : undefined;
  return {
    tick: state.tick,
    time: state.time,
    event: state.event,
    eventRemaining: state.eventRemaining,
    stationX: state.stationX,
    stationY: state.stationY,
    width: state.width,
    height: state.height,
    players: Array.from(state.players.values(), shipSnapshot),
    npcs: Array.from(state.npcs.values(), shipSnapshot),
    loot: Array.from(state.loot.values(), (item) => ({
      id: item.id,
      kind: item.kind,
      x: item.x,
      y: item.y,
      value: item.value,
      pending: item.pending,
      claimedBy: Array.from(item.claimedBy),
      shared: item.shared,
    })),
    projectiles: Array.from(state.projectiles.values(), (item) => ({
      id: item.id,
      x: item.x,
      y: item.y,
      angle: item.angle,
      kind: item.kind,
      ownerId: item.ownerId,
    })),
    combatEffects: Array.from(state.combatEffects.values(), (item) => ({
      id: item.id,
      kind: combatEffectKind(item.kind),
      ownerId: item.ownerId,
      x: item.x,
      y: item.y,
      radius: item.radius,
      remaining: item.remaining,
    })),
    publicEvents: Array.from(state.publicEvents.values(), (item) => ({
      hasDeadline: item.hasDeadline,
      id: item.id, type: publicEventTypeSchema.parse(item.type), title: item.title,
      terms: publicEventPresentationSchema.parse({ version: item.termsVersion, variantId: item.variantId,
        title: item.title, description: item.description, condition: item.condition,
        credits: item.credits, components: item.components,
        ...(item.prestigeReward ? { prestigeReward: item.prestigeReward } : {}),
        ...contributionSnapshot(item) }),
      ...captureSnapshot(item),
      workBlocked: item.workBlocked,
      sectorId: sectorIdSchema.parse(item.sectorId), status: eventStatusSchema.parse(item.status),
      x: item.x, y: item.y, progress: item.progress, goal: item.goal,
      participants: item.participants, scaledFor: item.scaledFor, remaining: item.remaining,
      objective: { name: item.objectiveName, x: item.objectiveX, y: item.objectiveY,
        kind: objectiveKindSchema.parse(item.objectiveKind), ...(item.targetId ? { targetId: item.targetId } : {}) },
    })),
    obstacles: Array.from(state.obstacles.values(), (item) => ({
      ...(item.resourceMaxHp > 0 ? {resourceHp: item.resourceHp, resourceMaxHp: item.resourceMaxHp} : {}),
      id: item.id,
      x: item.x,
      y: item.y,
      radius: item.radius,
    })),
    gates: Array.from(state.gates.values()).filter((item) => viewerId === undefined ||
      !TRAINING_GATE_FACTIONS[item.id] || (viewer?.sectorId === "training" &&
        (!viewer.factionId || TRAINING_GATE_FACTIONS[item.id] === viewer.factionId))).map((item) => ({
      id: item.id,
      name: item.name,
      x: item.x,
      y: item.y,
      radius: item.radius,
      toSectorId: item.toSectorId,
    })),
    regions: Array.from(state.regions.values(), (item) => ({
      factionId: item.factionId ? factionIdSchema.parse(item.factionId) : null,
      pvpAllowed: item.pvpAllowed, description: item.description, resourceSummary: item.resourceSummary,
      id: item.id,
      name: item.name,
      x: item.x,
      y: item.y,
      width: item.width,
      height: item.height,
      weather: item.weather,
      variant: item.variant,
    })),
    beacons: Array.from(state.beacons.values(), (item) => ({
      id: item.id, x: item.x, y: item.y, radius: item.radius, expiresAt: item.expiresAt,
    })),
  };
}

function contributionSnapshot(item: PublicEventState): Pick<PublicEventView["terms"], "contributionRules"> {
  if (item.termsVersion === "public-events-v3" && item.type !== "relay") {
    return { contributionRules: {
      damage: item.contributionDamage, healing: item.contributionHealing, tasks: item.contributionTasks,
    } };
  }
  if (item.contributionDamage !== 0 || item.contributionHealing !== 0 || item.contributionTasks !== 0)
    throw new Error("Legacy and relay events cannot expose PvE contribution rules");
  return {};
}

function captureSnapshot(item: PublicEventState): Pick<PublicEventView, "capture"> {
  if (item.type === "relay") {
    if (item.prestigeReward !== RELAY_PRESTIGE_ID) throw new Error("Relay prestige reward is required");
    return { capture: relayCaptureViewSchema.parse({
      factionId: item.captureFaction === "" ? null : item.captureFaction,
      contested: item.captureContested, radius: item.captureRadius,
    }) };
  }
  if (item.captureFaction !== "" || item.captureContested || item.captureRadius !== 0 || item.prestigeReward !== "")
    throw new Error("PvE events cannot expose relay capture fields");
  return {};
}

const sectorIdSchema = z.enum(SECTOR_IDS);
const eventStatusSchema = z.enum(["available", "active", "success", "failed"]) satisfies z.ZodType<PublicEventStatus>;
const objectiveKindSchema = z.enum(["travel", "combat", "collect", "return", "complete"]);

function combatEffectKind(kind: string): "repair-field" | "scan" | "intercept" | "rail-hit" {
  if (kind === "repair-field" || kind === "scan" || kind === "intercept" || kind === "rail-hit") return kind;
  throw new Error(`Invalid combat effect kind: ${kind}`);
}
